import { useTranslation } from 'react-i18next';
import { formatDecimalForDisplay, type D2041Document, type D2726Document, type T85Document } from '@lims/shared-types';
import { ReadOnlyField, Reading } from '../../components/ui/index.js';
import { specificGravityFormFor } from './SpecificGravityForm.js';
import { CardStateLabel, DeterminationCard, EnteredItem, SubSection } from './bench-parts.js';
import { ResultBlock, SummaryResult } from './specialized-form/index.js';

/**
 * A FROZEN ASTM D2726, D2041 OR AASHTO T85 RESULT, READ-ONLY — what a reviewer
 * and an approver act on: every reading as entered, every value and each
 * average as the server calculated them, the environment where the workbook
 * records it, and the remarks (D265, D274). It reads the same configuration
 * as the form, so the two never disagree on a field. Entered and calculated
 * values carry different marks (CLAUDE.md section 8); a value nobody recorded
 * reads as "not recorded", never as zero.
 *
 * One card per row, the shape the technician filled in (D278).
 */
export function SpecificGravityRecord({ document }: { readonly document: D2726Document | D2041Document | T85Document }) {
  const { t } = useTranslation();
  const config = specificGravityFormFor(document.templateKey);
  const notRecorded = t('common.notRecorded');
  if (!config) return <p className="text-body text-danger-700">{t('d2216.missingDocument')}</p>;
  const ns = config.ns;
  const entered = (value: string | null | undefined) => (
    <Reading value={value ?? null} provenance="entered" provenanceLabel={t('d2216.enteredLabel')} absentLabel={notRecorded} />
  );
  const calculated = (value: string | null | undefined, places: number, emphasis = false) => (
    <Reading
      value={formatDecimalForDisplay(value ?? null, places)}
      provenance="calculated"
      provenanceLabel={t('d2216.calculatedByServer')}
      absentLabel={t('d2216.notCalculated')}
      emphasis={emphasis}
    />
  );

  const info = ((document as { testInformation?: Record<string, string | null> }).testInformation ?? {}) as Record<
    string,
    string | null
  >;
  const rows = ((document as unknown as Record<string, unknown>)[config.rowsField] ?? []) as readonly Record<string, unknown>[];
  const summary = config.storedSummary(document);
  const environment = 'environment' in document ? document.environment : null;

  return (
    <div className="flex flex-col gap-stack" data-testid={`${ns}-record`}>
      {config.infoFields.length > 0 ? (
        <SubSection title={t('d2216.testInformationTitle')}>
          <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
            {config.infoFields.map((field) => (
              <ReadOnlyField key={field} label={t(`${ns}.${field}`)}>
                <span dir="auto">{info[field] ?? notRecorded}</span>
              </ReadOnlyField>
            ))}
          </div>
        </SubSection>
      ) : null}

      <SubSection title={t(`${ns}.rowsTitle`)}>
        {rows.length === 0 ? (
          <p className="text-body text-default-600" data-testid={`${ns}-record-table`}>
            {t(`${ns}.noRows`)}
          </p>
        ) : (
          <ol className="flex flex-col gap-stack" data-testid={`${ns}-record-table`}>
            {rows.map((row, index) => {
              const calculation = (row.calculation ?? null) as Record<string, string> | null;
              return (
                <DeterminationCard
                  key={String(row.id)}
                  testId={`${ns}-record-row`}
                  ariaLabel={t(`${ns}.rowNumbered`, { number: index + 1 })}
                  title={t(`${ns}.rowNumbered`, { number: index + 1 })}
                  state={
                    <CardStateLabel
                      state={calculation ? 'calculated' : 'incomplete'}
                      label={calculation ? t('d2216.calculatedByServer') : t('d2216.notCalculated')}
                    />
                  }
                  entered={
                    <>
                      {config.numberField ? (
                        <EnteredItem label={t(`${ns}.field.specimenNumber`)}>
                          {(row.specimenNumber as string | null) ?? notRecorded}
                        </EnteredItem>
                      ) : null}
                      {config.readingFields.map((field) => (
                        <EnteredItem key={field} label={t(`${ns}.field.${field}`)}>
                          {entered(row[field] as string | null)}
                        </EnteredItem>
                      ))}
                    </>
                  }
                  calculated={config.calculated.map(([key, places, emphasis]) => (
                    <ResultBlock key={key} label={t(`${ns}.calculated.${key}`)}>
                      {calculated(calculation?.[key], places, emphasis)}
                    </ResultBlock>
                  ))}
                />
              );
            })}
          </ol>
        )}
        <SummaryResult>
          {config.averages.map((average) => (
            <ResultBlock key={average.key} label={t(average.label)}>
              <span data-testid={config.averages.length === 1 ? `${ns}-record-average` : `${ns}-record-${average.key}`}>
                {calculated(summary[average.key], average.places, true)}
              </span>
            </ResultBlock>
          ))}
        </SummaryResult>
        <p className="text-caption text-default-600">{t(`${ns}.recordLegend`)}</p>
      </SubSection>

      {environment ? (
        <SubSection title={t('d2216.environmentTitle')}>
          <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
            <ReadOnlyField label={t('d2216.ambientTemperature')}>{entered(environment.ambientTemperature)}</ReadOnlyField>
            <ReadOnlyField label={t('d2216.relativeHumidity')}>{entered(environment.relativeHumidity)}</ReadOnlyField>
          </div>
          <ReadOnlyField label={t('d2216.remarks')}>
            <span dir="auto" className="whitespace-pre-wrap">{document.remarks ?? notRecorded}</span>
          </ReadOnlyField>
        </SubSection>
      ) : (
        <ReadOnlyField label={t('d2216.remarks')}>
          <span dir="auto" className="whitespace-pre-wrap">{document.remarks ?? notRecorded}</span>
        </ReadOnlyField>
      )}
    </div>
  );
}
